import { useState } from 'react';
import { getLocations } from 'src/services/client';
import {
  fetchAddressComponentsAtCoordinates,
  fetchCoordinatesForAddress,
} from './geocodeApi';
import { parseAddressComponents } from './helpers';
import {
  GeocodeCoordinates,
  LocationPrediction,
  ParsedAddress,
  UseLocationSearchProps,
  UseLocationSearchReturn,
} from './types';

interface LocationSearchState {
  location: string;
  predictions: LocationPrediction[];
}

export const useLocationSearch = ({
  onAddressSelected,
}: UseLocationSearchProps): UseLocationSearchReturn => {
  const [searchState, setSearchState] = useState<LocationSearchState>({
    location: '',
    predictions: [],
  });

  const setLocation = (location: string): void => {
    setSearchState((prev: LocationSearchState) => ({ ...prev, location }));
  };

  const setPredictions = (predictions: LocationPrediction[]): void => {
    setSearchState((prev: LocationSearchState) => ({ ...prev, predictions }));
  };

  const clearLocationSearch = (): void => {
    setSearchState({ location: '', predictions: [] });
  };

  const getLocation = async (value: string): Promise<void> => {
    setLocation(value);

    if (!value.trim()) {
      setPredictions([]);
      return;
    }

    try {
      const response: any = await getLocations(value);

      if (response.status !== 200) {
        console.error('Cannot find this address');
        setPredictions([]);
        return;
      }

      const responseJson: any = await response.json();
      const data: any = JSON.parse(responseJson);

      setPredictions(data.predictions || []);
    } catch (error) {
      console.error('Error fetching location predictions:', error);
      setPredictions([]);
    }
  };

  const onClickLocation = async (address: string): Promise<void> => {
    try {
      const coordinates: GeocodeCoordinates =
        await fetchCoordinatesForAddress(address);

      if (!coordinates) {
        console.error('No results found for this address');
        return;
      }

      const addressComponents =
        await fetchAddressComponentsAtCoordinates(coordinates);

      if (!addressComponents) {
        console.error('Cannot find detailed address information');
        return;
      }

      const parsedAddress: ParsedAddress =
        parseAddressComponents(addressComponents);

      onAddressSelected(parsedAddress);
      clearLocationSearch();
    } catch (error) {
      console.error('Unexpected error in onClickLocation:', error);
    }
  };

  return {
    location: searchState.location,
    predictions: searchState.predictions,
    setLocation,
    setPredictions,
    clearLocationSearch,
    getLocation,
    onClickLocation,
  };
};
